import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  HStack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import type { SpotPendingApprovalDate } from '~/hooks/bidding/useListBiddingSpotPendingApprovalDates'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { formatDateDisplay } from '~/models/dateTime'
import { isSpotRowPastDeadline } from '~/pages/SpotBidding/models/marketPhase'

type Props = {
  /**
   * 表示する受渡日ごとの承認待ちの申請数 ([[crossDatePendingDates]] で選択中・
   * 締切後の受渡日を除いたもの)。
   */
  pendingDates: SpotPendingApprovalDate[]
  /** 受渡日チップ押下。 その受渡日へ切り替える。 */
  onSelectDate: (date: Date) => void
  /**
   * ワークフローケース由来で受渡日が固定されているか (issue #7131 /
   * AC-WFW-006a)。 true のとき受渡日チップを disabled にし、 理由 tooltip
   * (`useWorkflowLockReasons` の `dateLockedReason`) を出す (= header の日付操作 UI と同じ
   * 契約。 チップも受渡日を変える導線のため対象)。 省略時は false (= 非固定)。
   */
  isDateLocked?: boolean
}

/** 日付を「その日の 00:00」 に落として比較できるようにする。 */
const startOfDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/**
 * 「他の受渡日に承認待ちがある」 ことを常時知らせるバナー (spec AC-BSP-095 /
 * #5538 Des 裁定 2026-08-18)。
 *
 * 受渡日を切り替えないと自分の申請が別の日で止まっていることに気づけない、 という
 * ドッグフーディングの困りごとへの対応。 **受渡日を切り替えなくても見える**ことが
 * 要件で (裁定: spec の「常時」 はその意味)、 スクロール追従までは求められていない。
 *
 * ## 数え方
 *
 * N は **承認申請の数** (デザイナー判断 2026-08-18)。 同一受渡日に複数の申請が並存
 * しうる (AC-079 の追加入札) ため、 受渡日の数で数えると実際に待っている件数より
 * 少なく見える。 その代わり **チップの数と N は一致しないことがある**。
 *
 * ## 既存バナーとの関係
 *
 * 選択中の受渡日ぶんは既存の `ApprovalStatusBanner` が出すので、 ここでは**出さない**
 * (二重表示を避ける)。 バナーは 2 本に分ける判断 (デザイナー判断 2026-08-18)。
 *
 * 見分けは **書き出し**「他の受渡日に…」 で付ける。 裁定は「書き出し・アイコンを
 * 変える」 だったが、 **アイコンは変えない**: DS `Alert` は status の icon を内包する
 * ので、 中に別の icon を足すと 1 つの alert に icon が 2 つ並ぶ。
 *
 * ## [仕様差異] 承認者 / 申請者で文言を分けていない
 *
 * 裁定は「承認者 =『承認してください』/ 申請者 =『承認待ちです』」 と文言を分ける
 * ことを求めているが、 **frontend から「閲覧者がこの申請の承認者か」 を判定できない**。
 * 役割を問わない 1 文言で出す。 AC-BSP-095 自体はこれで満たせる。
 *
 * 前提のうち「本人が誰か」 は解消済み: `GetCurrentSessionResponse.member_id` が
 * ログイン中の member_id を返す (issue #6594 の段階1・2)。 残っているのは
 * **突合する承認者の側**で、 承認申請の時点の承認者の写し (`stage_approvers`、
 * AC-BSP-098) は明細にしか無く、 このバナーが読む受渡日ごとの申請数には無い。
 * それが入るまでは文言を分けない。
 *
 * **文言に二人称 (「あなたの承認待ち」) を使わないのはこのため。** N は
 * 「**組織内で**承認待ちになっている申請の件数」 であって「閲覧者が承認すべき件数」
 * ではない: 集計 RPC の WHERE は `organization_id`・承認状態・受渡日だけで、 承認者による
 * 絞り込みはどの層にも無い
 * (`bidding_spot_bid_approval_request_repository.go` CountPendingByTargetDateFrom)。 自分が出した申請も、
 * 承認権限を持たない人が見た時も同じ数字が出るので、 二人称を足すと事実と食い違う
 * (2026-08-20 デザイナーからの「自分の承認待ちという意味か」 という問いへの回答)。
 *
 * ## 拾う範囲
 *
 * - **承認待ち (PENDING) のみ。** 差戻しは「誰の申請か」 を画面が判定できないため
 *   含めない (含めると組織全体の差戻し件数を出すことになる)。
 * - **入札締切を過ぎた受渡日は数えない** (AC-BSP-095)。 その受渡日の承認待ちは
 *   AC-BSP-099 で失効になり、 承認も取消もできないため、 気づかせる先が無い。
 *   締切後かどうかは受渡日だけで決め、 申請の承認状態が戻るのを待たない。
 * - **通常入札のみ** (ブロック入札は別 RPC。 必要なら後から足せる)。
 */
/**
 * 受渡日ごとの承認待ちの申請数から、 選択中の受渡日 (既存の承認バナーが出す) と
 * 入札締切を過ぎた受渡日を除く。 backend は取得時点で締切前の受渡日だけを返すが、
 * polling の間に締切を跨いだ受渡日を画面の分単位 tick で外すため、 ここでも判定する。
 *
 * 本 component は該当 0 件で `null` を返すため、 **描画されるかどうかを呼び出し元が
 * 知る手段が無い**。 ステータス帯 ([[SpotStatusHub]]) は中身が 1 つも無い時に器ごと
 * 出さない必要があるので (器だけ残ると親 flex の `gap` を 1 つぶん消費する)、
 * 判定をここに切り出して component と呼び出し元で共有する。
 */
export function crossDatePendingDates(
  pending: SpotPendingApprovalDate[],
  currentTargetDate: Date,
  now: Date,
): SpotPendingApprovalDate[] {
  const currentKey = startOfDay(currentTargetDate)
  return pending.filter(
    (d) =>
      startOfDay(d.targetDate) !== currentKey &&
      !isSpotRowPastDeadline(d.targetDate, now),
  )
}

/** 見出しの件数 N は申請数 (チップの数 = 受渡日の数とは一致しないことがある)。 */
const totalRequestCount = (pending: SpotPendingApprovalDate[]): number =>
  pending.reduce((sum, d) => sum + d.requestCount, 0)

/**
 * バナーの**中身だけ**。 上部ステータス帯は同じ status の警告を 1 枚の Alert に
 * まとめて箇条書きにするため (デザイナー指示 2026-08-31)、 `Alert` の器を持たない
 * 形が要る。 該当 0 件なら `null` を返すのは器付き版と同じ。
 */
export const CrossDatePendingApprovalBody = ({
  pendingDates,
  onSelectDate,
  isDateLocked = false,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()

  if (pendingDates.length === 0) return null

  return (
    <Box flex={1}>
      <Text fontWeight="semibold">
        {t('page:biddingSpot.crossDatePending.title', {
          count: totalRequestCount(pendingDates),
        })}
      </Text>
      {/* 件数を告げるだけだと**次に何をすればいいか**が読めない (2026-08-25 Des 指摘)。
          チップが「押すと受渡日が切り替わるボタン」 であることは見ただけでは分からない
          ので、 一言添える。

          文言が「承認してください」 でないのは役割を出し分けられないため
          (上の [仕様差異] 参照)。 また GC 済みの受渡日へ飛んだ先では承認・取消が
          できないので「承認できます」 とも書けない。 役割にも GC にも依らず常に真な
          「切り替わって内容と操作を確認できる」 だけを書く。 */}
      <Text marginTop={1} fontSize="sm">
        {t('page:biddingSpot.crossDatePending.action')}
      </Text>
      <PendingDateChips
        isDateLocked={isDateLocked}
        onSelectDate={onSelectDate}
        pendingDates={pendingDates}
      />
    </Box>
  )
}

/**
 * 承認待ちのある受渡日へ切り替えるチップ。 チップは受渡日ごとに 1 つ (同じ日に複数申請が
 * あっても 1 チップ)。 日付順に並べる。
 */
const PendingDateChips = ({
  pendingDates,
  onSelectDate,
  isDateLocked,
}: {
  pendingDates: SpotPendingApprovalDate[]
  onSelectDate: Props['onSelectDate']
  isDateLocked: boolean
}): React.ReactElement => {
  const { dateLockedReason } = useWorkflowLockReasons()
  const dateKeys = [
    ...new Set(pendingDates.map((d) => startOfDay(d.targetDate))),
  ].sort((a, b) => a - b)
  return (
    <HStack alignItems="center" flexWrap="wrap" gap={2} marginTop={2}>
      {dateKeys.map((key) => {
        const chip = (
          <TertiaryButton
            disabled={isDateLocked}
            onClick={() => {
              onSelectDate(new Date(key))
            }}
            size="sm"
          >
            {formatDateDisplay(new Date(key))}
          </TertiaryButton>
        )
        // チップも受渡日を切り替える導線のため、 固定中は disabled + 理由
        // tooltip にする (issue #7131 / AC-WFW-006a: 対象日操作 UI は
        // header の 4 コントロールに限らない)。
        return (
          <ButtonTooltip
            key={key}
            content={isDateLocked ? dateLockedReason : undefined}
          >
            {chip}
          </ButtonTooltip>
        )
      })}
    </HStack>
  )
}

/**
 * 器付き。 単独で 1 枚の Alert として出す呼び出し用に残す。
 *
 * `flexShrink={0}`: 呼び出し元は高さ拘束された column flex なので、 既定だと
 * 押し潰され、 DS `Alert` の `overflow: hidden` で中身が切り落とされる
 * (2026-08-19 デザイナー指摘)。
 */
export const CrossDatePendingApprovalBanner = ({
  pendingDates,
  onSelectDate,
  isDateLocked = false,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  if (pendingDates.length === 0) return null
  // 見出しは `title`、 案内とチップは `description` に渡す (UI 辞書 alert-placement.md §4
  // 「React (DS `Alert`) での組み方」)。 チップは受渡日の数だけ並ぶので右端の導線にしない。
  return (
    <Alert
      flexShrink={0}
      description={
        <>
          <Text>{t('page:biddingSpot.crossDatePending.action')}</Text>
          <PendingDateChips
            isDateLocked={isDateLocked}
            onSelectDate={onSelectDate}
            pendingDates={pendingDates}
          />
        </>
      }
      status="warning"
      title={t('page:biddingSpot.crossDatePending.title', {
        count: totalRequestCount(pendingDates),
      })}
    />
  )
}
